import React, { memo, useEffect, useState } from 'react';
import { cloneDeep, pick, set } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';

import styles from './WifiPage.module.css';
import { useAuth } from '../../context/Auth';
import Placeholder from '../../components/placeholder/Placeholder';
import PrimaryButton from '../../components/primaryButton/PrimaryButton';
import {
  logInLabel,
  deviceSetupLoading,
  deviceSetupLogin,
  wifiDownloadExplanation,
  wifiDownloadStartedMessage,
  wifiLoadToUsbDrivePlugDriveMessage,
  wifiFollowMiniscreenMessage,
  wifiLoadToUsbDriveCopyFileMessage,
  dashboardPath,
  continueLabel,
  wifiFinishedTitle,
  wifiFinishedExplanation,
  wifiAfterDownloadTitle,
  wifiPageTitle,
  wifiPageDescription,
  returnToDashboardLabel,
  deviceSetupDownloadPrompt,
  deviceSetupDownloadLabel,
  downloadFinishedLabel,
  deviceSetupLoadToUsbDrivePlugDrivePrompt,
  deviceSetupLoadToUsbDriveCopyFilePrompt,
  deviceSetupLoadToUsbDriveEjectPrompt,
  deviceSetupLoadToUsbDriveEjectMessage,
  deviceSetupPowerOnPrompt,
  deviceSetupPlugPowerMessage,
  deviceSetupPlugUsbDrivePrompt,
  deviceSetupPlugUsbDriveMessage,
  deviceSetupFollowMiniscreenPrompt,
} from '../../strings';
import FlowStepper from '../../components/flowStepper/FlowStepper';
import readyCharacter from '../../assets/images/ready-character.png';
import successScreenIcon from '../../assets/images/tutoring-graphic.svg';
import plugUsbDriveToLaptop from '../../assets/images/deviceSetup/usb-drive-plug-laptop.png';
import transferFileToUsbDrive from '../../assets/images/deviceSetup/transfer-file-to-usb-drive.png';
import ejectUsbDriveFromLaptop from '../../assets/images/deviceSetup/usb-drive-unplug-laptop.png';
import connectPowerSupply from '../../assets/images/deviceSetup/power-supply.svg';
import plugUsbDriveToPiTop from '../../assets/images/deviceSetup/plug-usb-to-pitop.png';
import miniscreenApp from '../../assets/images/deviceSetup/pt-usb-setup.gif';
import WifiForm, {
  defaultPreset,
  networkFields,
  NetworkType,
  WifiFormData,
  JSONFile,
  validateRequiredFiles,
} from '../../components/wifiForm/WifiForm';
import {
  createTarArchiveOfSingleJsonAndAdditionalFiles,
  downloadTarGz,
} from '../../utils/downloadHelpers';

const base64ToUint8Array = (base64: string) => {
  if (!base64) return new Uint8Array();

  const commaIndex = base64.indexOf(',');
  const data = commaIndex !== -1 ? base64.slice(commaIndex + 1) : base64;
  try {
    const binaryString = atob(data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch {
    return new Uint8Array();
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const removeEmptyStrings = (obj: any) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (obj[key] === '') {
      // eslint-disable-next-line no-param-reassign
      delete obj[key];
    } else if (
      typeof obj[key] === 'object' &&
      obj[key] !== undefined &&
      obj[key] !== null
    ) {
      removeEmptyStrings(obj[key]);
    }
  }
  return obj;
};

// Single-file upload fields are backed by arrays with a single element.
// This implmentation allows us to use the same component for both single-file and multi-file uploads,
// while keeping the logic relatively simple.
// This function unwraps the single-file upload fields from arrays to their actual values.
const unwrapSingleFileFields = (config: any): any => {
  const authData = config?.authentication?.data;

  const singleFileFields = ['ca_cert', 'user_cert', 'user_private_key'];

  singleFileFields.forEach((field) => {
    // If the field is an empty array, delete it
    if (Array.isArray(authData?.[field]) && authData[field].length === 0) {
      delete authData[field];
    }

    // If the field is an array with a single element, unwrap it
    if (Array.isArray(authData?.[field]) && authData[field].length === 1) {
      // eslint-disable-next-line prefer-destructuring
      authData[field] = authData[field][0];
    }
  });

  return config;
};

const filterForJsonExport = (formData: WifiFormData) => {
  const fields =
    formData.authentication.type === NetworkType.WPA_ENTERPRISE
      ? networkFields[NetworkType.WPA_ENTERPRISE][
          formData.authentication.data.authentication
        ]
      : networkFields[formData.authentication.type];

  const jsonKeysToInclude = [
    'authentication.type',
    ...Object.keys(fields),
  ].filter((key) => key !== 'authentication.data.additional_ca_certs');

  const network = pick(formData, jsonKeysToInclude);
  return removeEmptyStrings(unwrapSingleFileFields(network));
};

const fetchAsText = async (url: string) => {
  if (!url) {
    throw new Error('No URL provided');
  }
  try {
    const response = await fetch(url);
    const text = await response.text();
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}`);
    }
    return text;
  } catch (error) {
    throw new Error(`Failed to fetch ${url}`);
  }
};

const getPackageFilenames = () => {
  return [
    'python3-pitop_0.35.0-4_all.deb',
    'python3-pitop-battery_0.35.0-4_all.deb',
    'python3-pitop-camera_0.35.0-4_all.deb',
    'python3-pitop-cli_0.35.0-4_all.deb',
    'python3-pitop-common_0.35.0-4_all.deb',
    'python3-pitop-core_0.35.0-4_all.deb',
    'python3-pitop-display_0.35.0-4_all.deb',
    'python3-pitop-doc_0.35.0-4_all.deb',
    'python3-pitop-full_0.35.0-4_all.deb',
    'python3-pitop-keyboard_0.35.0-4_all.deb',
    'python3-pitop-miniscreen_0.35.0-4_all.deb',
    'python3-pitop-pma_0.35.0-4_all.deb',
    'python3-pitop-processing_0.35.0-4_all.deb',
    'python3-pitop-robotics_0.35.0-4_all.deb',
    'python3-pitop-simulation_0.35.0-4_all.deb',
    'python3-pitop-system_0.35.0-4_all.deb',
    'further-link_5.11.1-1_all.deb',
    'pi-topd_5.7.0-1_all.deb',
    'pi-topd-extra_5.7.0-1_all.deb',
    'pi-topd-notify_5.7.0-1_all.deb',
    'pi-topd-screen-blank_5.7.0-1_all.deb',
    'pi-top-usb-setup_0.3.1-3_all.deb',
    'pt-firmware-updater_4.2.0-1_all.deb',
    'pt-os-update-monitor_0.1.0-1_all.deb',
    'pt-os-web-portal_0.26.1-1_all.deb',
    'pt-os-web-portal-desktop_0.26.1-1_all.deb',
    'python3-distro-info_1.5_all.deb',
    'unattended-upgrades_2.9.1nmu3_all.deb',
  ];
};

const getFileContent = async (
  path: string,
  packageName: string
): Promise<string | Uint8Array> => {
  const { default: file } = await import(`../../assets/other/${path}`);
  if (packageName.endsWith('.deb')) {
    return base64ToUint8Array(file);
  }
  return fetchAsText(file);
};

const getUpdateContentForTar = async (
  rootFolder: string
): Promise<{ filepath: string; content: string | Uint8Array }[]> => {
  const filesToInclude = ['Release', 'Packages', ...getPackageFilenames()];

  const updateFiles = await Promise.all(
    filesToInclude.map(async (packageName) => {
      const folder = 'updates_bookworm';
      const path = `${rootFolder}/${folder}/${packageName}`;
      return {
        filepath: path,
        content: await getFileContent(path, packageName),
      };
    })
  );

  return updateFiles.flat();
};

const preparePiTopSetupTar = async (formData: WifiFormData, email: string) => {
  const additionalCerts = formData.authentication.data.additional_ca_certs;
  const network = filterForJsonExport(formData);
  const jsonString = JSON.stringify({ email, network }, null, 2);

  const rootFolder = 'pi-top-usb-setup';
  const certs = additionalCerts.map((cert: JSONFile) => ({
    filepath: `${rootFolder}/certificates/ca-certificates/${cert.filename}`,
    content: cert.content,
  }));
  const updateFiles = await getUpdateContentForTar(rootFolder);

  const tarBytes = await createTarArchiveOfSingleJsonAndAdditionalFiles(
    `${rootFolder}/pi-top_config.json`,
    jsonString,
    [...certs, ...updateFiles]
  );
  return tarBytes;
};

const WifiPage = () => {
  const { authenticated, isReady, login, myAccount } = useAuth();
  const userEmail = myAccount?.email || '';
  const navigate = useNavigate();

  const [formData, setFormData] = useState<WifiFormData>(
    cloneDeep(defaultPreset)
  );
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [uploadWarnings, setUploadWarnings] = useState<
    Record<string, string[]>
  >({});

  const steps = [
    {
      banner: { src: successScreenIcon, alt: 'illustration' },
      className: styles.smallerBanner,
      prompt: deviceSetupDownloadPrompt,
      explanation: wifiDownloadExplanation,
      nextButton: {
        label: deviceSetupDownloadLabel,
        onClick: async () => {
          const tarBytes = await preparePiTopSetupTar(formData, userEmail);

          downloadTarGz(tarBytes);
        },
      },
      showSkip: false,
    },
    {
      banner: { src: successScreenIcon, alt: 'illustration' },
      className: styles.smallerBanner,
      prompt: wifiAfterDownloadTitle,
      explanation: wifiDownloadStartedMessage,
      nextButton: {
        label: downloadFinishedLabel,
      },
      showSkip: false,
    },
    {
      banner: { src: plugUsbDriveToLaptop, alt: 'plug-usb-drive-to-laptop' },
      prompt: deviceSetupLoadToUsbDrivePlugDrivePrompt,
      explanation: wifiLoadToUsbDrivePlugDriveMessage,
      nextButton: {
        label: continueLabel,
      },
      showSkip: false,
    },
    {
      banner: { src: transferFileToUsbDrive, alt: 'copy-file-to-usb-drive' },
      prompt: deviceSetupLoadToUsbDriveCopyFilePrompt,
      explanation: wifiLoadToUsbDriveCopyFileMessage,
      nextButton: {
        label: continueLabel,
      },
      showSkip: false,
    },
    {
      banner: { src: ejectUsbDriveFromLaptop, alt: 'eject-usb-drive' },
      prompt: deviceSetupLoadToUsbDriveEjectPrompt,
      explanation: deviceSetupLoadToUsbDriveEjectMessage,
      nextButton: {
        label: continueLabel,
      },
      showSkip: false,
    },
    {
      banner: { src: connectPowerSupply, alt: 'power-supply' },
      prompt: deviceSetupPowerOnPrompt,
      explanation: deviceSetupPlugPowerMessage,
      nextButton: {
        label: continueLabel,
      },
      showSkip: false,
    },
    {
      banner: { src: plugUsbDriveToPiTop, alt: 'plug-usb-drive-to-pi-top' },
      prompt: deviceSetupPlugUsbDrivePrompt,
      explanation: deviceSetupPlugUsbDriveMessage,
      nextButton: {
        label: continueLabel,
      },
      showSkip: false,
    },
    {
      banner: { src: miniscreenApp, alt: 'follow-miniscreen-instructions' },
      prompt: deviceSetupFollowMiniscreenPrompt,
      explanation: wifiFollowMiniscreenMessage,
      nextButton: {
        label: continueLabel,
      },
    },
    {
      banner: { src: readyCharacter, alt: 'ready-character' },
      prompt: wifiFinishedTitle,
      explanation: wifiFinishedExplanation,
      nextButton: {
        label: returnToDashboardLabel,
        onClick: () => {
          navigate(`/${dashboardPath}`);
        },
      },
    },
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const pageSearchparam = searchParams.get('page');

  useEffect(() => {
    if (!isFormSubmitted && pageSearchparam) {
      const next = new URLSearchParams(searchParams);
      next.delete('page');
      setSearchParams(next);
    }
  }, [pageSearchparam, searchParams, isFormSubmitted, setSearchParams]);

  const clearUploadErrorsAndWarnings = (id: string) => {
    // Certain changes should wipe the upload errors and warnings for ALL fields.
    // Particularly, when the user switches to a different auth type, e.g. from TLS to PEAP,
    // which requires different fields so the old errors are no longer relevant.
    // Clearing obsolete errors is important to avoid the form getting stuck in an invalid (unsubmittable) state.
    if (
      id === 'authentication.type' ||
      id === 'authentication.data.authentication'
    ) {
      setUploadErrors({});
      setUploadWarnings({});
      return;
    }

    // Clear errors and warnings for the specific field that was changed
    setUploadErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });

    setUploadWarnings((prev) => {
      const newWarnings = { ...prev };
      delete newWarnings[id];
      return newWarnings;
    });
  };

  const handleChange = (value: string | boolean | JSONFile[], id: string) => {
    set(formData, id, value);
    setFormData({ ...formData });

    clearUploadErrorsAndWarnings(id);
  };

  if (!authenticated) {
    return (
      <>
        <div className={styles.description}>
          {deviceSetupLogin}
          <PrimaryButton onClick={() => login()} unstyled>
            {logInLabel}
          </PrimaryButton>
        </div>
      </>
    );
  }

  if (!isReady) {
    return <Placeholder isLoading message={deviceSetupLoading} />;
  }

  if (!isFormSubmitted)
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <h1 className={styles.title}>{wifiPageTitle}</h1>
          <p className={styles.description}>{wifiPageDescription}</p>
        </div>

        <WifiForm
          formData={formData}
          onChange={handleChange}
          onChangePreset={(preset) => {
            setFormData(cloneDeep(preset));
            setUploadErrors({});
            setUploadWarnings({});
          }}
          onSubmit={() => {
            const missingFileErrors = validateRequiredFiles(formData);
            setUploadErrors(missingFileErrors);

            // If there are no errors, set the form as submitted
            if (Object.keys(missingFileErrors).length === 0) {
              setIsFormSubmitted(true);
            }
          }}
          userEmail={userEmail}
          uploadErrors={uploadErrors}
          uploadWarnings={uploadWarnings}
          addUploadWarning={(address: string, message: string) => {
            const oldMessages = uploadWarnings[address] || [];
            // Avoid duplicate warnings
            const newMessages = Array.from(new Set([...oldMessages, message]));
            setUploadWarnings({ ...uploadWarnings, [address]: newMessages });
          }}
          dismissUploadWarning={(address: string, message: string) => {
            setUploadWarnings((prev) => {
              const oldMessages = prev[address] || [];
              const newMessages = oldMessages.filter((msg) => msg !== message);
              return { ...prev, [address]: newMessages };
            });
          }}
        />
      </div>
    );

  return (
    <FlowStepper
      layoutPropsArray={steps}
      onPageZeroBack={() => {
        // reset the form
        setIsFormSubmitted(false);
      }}
    />
  );
};

export default memo(WifiPage);
